import React from 'react';
import { TouchableOpacity, Platform, View, StyleSheet } from 'react-native';
import { useTheme, useThemeName, Text } from '@tamagui/core';
import { XStack, YStack } from '@tamagui/stacks';
import { Home, MessageCircle, Plus, User, Settings } from 'lucide-react-native';
import type { BottomTabBarProps } from '@react-navigation/bottom-tabs';
import { COLORS } from '@/constants';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useChats } from '@/hooks/useFirebaseChat';
import { useApplicationNotifications } from '@/hooks/useApplicationNotifications';

const TabBar: React.FC<BottomTabBarProps> = ({
  state,
  descriptors,
  navigation,
}) => {
  const theme = useTheme();
  const themeName = useThemeName();
  // Always call the hook, but use fallback values on web
  const safeAreaInsets = useSafeAreaInsets();
  const insets = Platform.OS === 'web' ? { bottom: 0 } : safeAreaInsets;
  
  // Get unread chat count
  const { totalUnread } = useChats();
  
  // Get new applications count
  const { totalNewCount: newApplicationsCount } = useApplicationNotifications();

  const isDark = themeName === 'dark';
  const primaryHex = theme.primary?.val ?? theme.blue10?.val ?? COLORS.PRIMARY;
  const mutedIconHex = theme.color10?.val ?? (isDark ? '#a1a1aa' : '#71717a');

  const getTabIcon = (
    routeName: string,
    focused: boolean,
    size: number = 26,
  ) => {
    const isCreateButton = routeName === 'Create';
    const iconColor = isCreateButton
      ? 'white'
      : focused
      ? (isDark ? primaryHex : COLORS.PRIMARY)
      : (isDark ? mutedIconHex : theme.color10?.val ?? '#9CA3AF');

    switch (routeName) {
      case 'Home':
        return <Home size={size} color={iconColor} />;
      case 'Chat':
        return <MessageCircle size={size} color={iconColor} />;
      case 'Create':
        return <Plus size={size} color={iconColor} />;
      case 'Profile':
        return <User size={size} color={iconColor} />;
      case 'Settings':
        return <Settings size={size} color={iconColor} />;
      default:
        return <Settings size={size} color={iconColor} />;
    }
  };

  const getTabButtonStyle = (
    isCreateButton: boolean,
    isFocused: boolean,
    isDark: boolean,
    primaryHex: string,
  ) => ({
    alignItems: 'center' as const,
    justifyContent: 'center' as const,
    width: isCreateButton ? 50 : 45,
    height: isCreateButton ? 50 : 45,
    borderRadius: isCreateButton ? 25 : 22,
    backgroundColor: isCreateButton
      ? (isDark ? primaryHex : COLORS.PRIMARY)
      : isFocused
      ? (isDark ? `${primaryHex}15` : COLORS.PRIMARY + '15')
      : 'transparent',
    shadowColor: isCreateButton ? 'black' : undefined,
    shadowOffset: isCreateButton ? { width: 0, height: 4 } : undefined,
    shadowOpacity: isCreateButton ? 0.25 : undefined,
    shadowRadius: isCreateButton ? 12 : undefined,
    elevation: isCreateButton ? 8 : 0,
    marginHorizontal: 4,
  });

  // Web version - hide bottom tab bar (navigation is in WebHeader)
  if (Platform.OS === 'web') {
    return null;
  }

  // Mobile version - absolutely positioned
  return (
    <YStack
      backgroundColor="transparent"
      position="absolute"
      bottom={insets.bottom}
      left={0}
      right={0}
      paddingBottom={0}
    >
      <XStack
        backgroundColor={isDark ? '$backgroundStrong' : 'white'}
        marginHorizontal="$2"
        borderRadius="$12"
        borderWidth={1}
        borderColor={isDark ? '#4b5563' : '#e5e7eb'}
        paddingVertical="$4"
        paddingHorizontal="$4"
        height={70}
        justifyContent="space-around"
        alignItems="center"
      >
        {state.routes.map((route: any, index: number) => {
          const { options } = descriptors[route.key];
          const isFocused = state.index === index;

          const onPress = () => {
            const event = navigation.emit({
              type: 'tabPress',
              target: route.key,
              canPreventDefault: true,
            });

            if (!isFocused && !event.defaultPrevented) {
              navigation.navigate(route.name);
            }
          };

          const isCreateButton = route.name === 'Create';

          return (
            <TouchableOpacity
              key={route.key}
              accessibilityRole="button"
              accessibilityState={isFocused ? { selected: true } : {}}
              accessibilityLabel={options.tabBarAccessibilityLabel}
              onPress={onPress}
              style={getTabButtonStyle(isCreateButton, isFocused, isDark, primaryHex)}
            >
              <View>
                {getTabIcon(route.name, isFocused)}
                {route.name === 'Chat' && totalUnread > 0 && (
                  <View style={styles.badge}>
                    <Text color="white" fontSize={10} fontWeight="700">
                      {totalUnread > 99 ? '99+' : totalUnread}
                    </Text>
                  </View>
                )}
                {route.name === 'Settings' && newApplicationsCount > 0 && (
                  <View style={styles.badge}>
                    <Text color="white" fontSize={10} fontWeight="700">
                      {newApplicationsCount > 99 ? '99+' : newApplicationsCount}
                    </Text>
                  </View>
                )}
              </View>
            </TouchableOpacity>
          );
        })}
      </XStack>
    </YStack>
  );
};

const styles = StyleSheet.create({
  badge: {
    position: 'absolute',
    top: -4,
    right: -8,
    backgroundColor: COLORS.ERROR,
    borderRadius: 10,
    minWidth: 18,
    height: 18,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 4,
  },
});

const webStyles = StyleSheet.create({
  container: {
    paddingBottom: 16,
    paddingTop: 8,
  },
});

export default TabBar;
